import { locale as config, files } from 'config';
import messages from './messages';
import { getMediaPath, getSearchParam } from 'utils/params';
import { MEDIA_ROOT_URL, ROUTER } from 'utils/constants';

const RTL_LOCALES = ['ar', 'dv', 'fa', 'he'];
const FALLBACK_LOCALE = 'en';

// Any Arabic/Persian script (incl. presentation forms) marks RTL content.
const RTL_SCRIPT = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
const LATIN_SCRIPT = /[A-Za-z]/;

// Recording id embedded in the playback URL: <40 hex/lower>-<13 digit epoch>.
const RECORD_ID_REGEX = /[a-z0-9]{40}-[0-9]{13}/;

// Meeting create() params surface as <key> elements under <meta>. If the
// integration tags the meeting language, honour it before auto-detecting.
const LOCALE_META_KEYS = [
  'bbb-recording-locale',
  'recording-locale',
  'playback-locale',
  'bbb-context-locale',
  'locale',
  'language',
  'lang',
];

const FETCH_TIMEOUT = 4000;

const localeToFile = (locale) => locale.replace('-', '_');

const fileToLocale = (locale) => locale.replace('_', '-');

const setDirection = (language) => {
  if (RTL_LOCALES.includes(language)) {
    document.body.parentNode.setAttribute('dir', 'rtl');
  } else {
    document.body.parentNode.setAttribute('dir', 'ltr');
  }
};

// Map an arbitrary locale tag to a messages file we actually ship, trying the
// full tag first (fa-IR -> fa_IR) then the bare language (fa).
const resolveFile = (locale) => {
  if (!locale) return null;

  const file = localeToFile(locale.trim());
  if (messages[file]) return file;

  const [language] = file.split('_');
  if (messages[language]) return language;

  return null;
};

const finalize = (file) => {
  const [language] = file.split('_');
  setDirection(language);

  return fileToLocale(file);
};

// Build the media URL for a recording file the same way utils/data does, but
// without depending on already-loaded metadata (we run before the player).
const buildRecordingFileURL = (recordId, file) => {
  const rootUrl = MEDIA_ROOT_URL ? MEDIA_ROOT_URL : '/presentation';
  const mediaPath = getMediaPath();

  let fileUrl = `${recordId}/${file}`;
  if (mediaPath) fileUrl = `${mediaPath}/${fileUrl}`;

  return `${rootUrl}/${fileUrl}`;
};

const getRecordIdFromPath = () => {
  const match = window.location.pathname.match(RECORD_ID_REGEX);

  return match ? match[0] : null;
};

const safeFetchText = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;

    return await response.text();
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

// Recording content (metadata, chat, notes) stores non-ASCII as HTML/XML
// entities (e.g. &#x627;), so decode before testing the script.
const decodeEntities = (text) => {
  const element = document.createElement('textarea');
  element.innerHTML = text;

  return element.value;
};

const extractLocaleTag = (xml) => {
  for (const key of LOCALE_META_KEYS) {
    const match = xml.match(new RegExp(`<${key}>([^<]+)</${key}>`, 'i'));
    if (match && match[1].trim()) return match[1].trim();
  }

  return null;
};

const detectScriptLocale = (text) => {
  if (RTL_SCRIPT.test(text)) return 'fa';
  if (LATIN_SCRIPT.test(text)) return 'en';

  return null;
};

const localeFromRecording = async (recordId) => {
  const metaText = await safeFetchText(buildRecordingFileURL(recordId, files.metadata));
  if (!metaText) return null;

  // (a) Explicit language tag set at meeting creation, if present.
  const file = resolveFile(extractLocaleTag(metaText));
  if (file) return file;

  // (b) Auto-detect from content. The meeting title alone usually settles it;
  // only sample the heavier chat/notes/slide text when the title is not RTL.
  let sample = decodeEntities(metaText);
  if (!RTL_SCRIPT.test(sample)) {
    const extra = await Promise.all(
      [files.notes, files.chat, files.alternates]
        .map((f) => safeFetchText(buildRecordingFileURL(recordId, f)))
    );
    sample += ` ${decodeEntities(extra.filter(Boolean).join(' '))}`;
  }

  return resolveFile(detectScriptLocale(sample));
};

// Resolve the playback UI locale. Precedence:
//   1. ?locale= query param (explicit override)
//   2. recording language (meta tag, then content auto-detection)
//   3. configured default (fa_IR)
//   4. browser language, then en
const resolveLocale = async () => {
  const param = getSearchParam('locale');
  if (param) {
    const file = resolveFile(param);
    if (file) return finalize(file);
  }

  if (ROUTER) {
    const recordId = getRecordIdFromPath();
    if (recordId) {
      try {
        const file = await localeFromRecording(recordId);
        if (file) return finalize(file);
      } catch (error) {
        // Fall through to the configured default.
      }
    }
  }

  return finalize(
    resolveFile(config.default) || resolveFile(navigator.language) || FALLBACK_LOCALE
  );
};

const getMessages = (locale) => {
  const file = localeToFile(locale);
  if (file !== FALLBACK_LOCALE) {
    return Object.assign(messages[FALLBACK_LOCALE], messages[file]);
  }

  return messages[file];
};

export {
  resolveLocale,
  getMessages,
}
